import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { getProduct, createSample, createQuote, saveSupplier } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useLocale } from '@/contexts/LocaleContext';
import OrderForm from '@/components/orders/OrderForm';
import ProPaywall from '@/components/ProPaywall';
import PublicHeader from '@/components/layout/PublicHeader';
import { 
  ArrowLeft, 
  Building2, 
  MapPin, 
  Package,
  TestTube,
  FileText,
  Sparkles,
  Bookmark,
  CheckCircle2,
  ImageIcon,
  Loader2,
  ShoppingCart
} from 'lucide-react';
import { toast } from 'sonner';

const ProductDetail = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { isUSA } = useLocale();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [sampleDialogOpen, setSampleDialogOpen] = useState(false);
  const [quoteDialogOpen, setQuoteDialogOpen] = useState(false);
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);

  const [sampleForm, setSampleForm] = useState({
    shipping_address: '',
    quantity: 1,
    unit: 'metros',
    urgency: 'normal',
    comments: ''
  });

  const [quoteForm, setQuoteForm] = useState({
    quantity: '',
    unit: 'metros',
    specifications: '',
    desired_composition: '',
    desired_weight: '',
    color: '',
    required_date: '',
    delivery_city: '',
    comments: ''
  });

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const data = await getProduct(productId);
        setProduct(data);
      } catch (error) {
        toast.error(isUSA ? 'Product not found' : 'Producto no encontrado');
        navigate('/search');
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [productId, navigate, isUSA]);

  const handleSampleSubmit = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      toast.error(isUSA ? 'Please log in' : 'Debes iniciar sesión');
      navigate('/login');
      return;
    }

    setSubmitting(true);
    try {
      await createSample({
        product_id: productId,
        company_id: product.company_id,
        ...sampleForm
      });
      toast.success('Solicitud de muestra enviada');
      setSampleDialogOpen(false);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      toast.error(isUSA ? 'Please log in' : 'Debes iniciar sesión');
      navigate('/login');
      return;
    }

    setSubmitting(true);
    try {
      await createQuote({
        target_company_ids: [product.company_id],
        product_type: product.name,
        quantity: parseInt(quoteForm.quantity),
        ...quoteForm
      });
      toast.success(isUSA ? 'Quote request sent' : 'Solicitud de cotización enviada');
      setQuoteDialogOpen(false);
    } catch (error) {
      if (error.status === 403) {
        setQuoteDialogOpen(false);
        setShowPaywall(true);
      } else {
        toast.error(error.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveSupplier = async () => {
    if (!isAuthenticated) {
      toast.error('Debes iniciar sesión');
      navigate('/login');
      return;
    }

    try {
      await saveSupplier(product.company_id);
      toast.success('Proveedor guardado');
    } catch (error) {
      toast.error(error.message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!product) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Unified Public Header */}
      <PublicHeader showBack={true} />
      
      <div className="container-app py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Images */}
          <div>
            <div className="aspect-square bg-white rounded-lg border border-slate-200 overflow-hidden mb-4">
              {product.images && product.images.length > 0 ? (
                <img 
                  src={product.images[selectedImage]} 
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <ImageIcon className="w-24 h-24 text-slate-300" />
                </div>
              )}
            </div>
            {product.images && product.images.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {product.images.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`w-20 h-20 rounded-lg overflow-hidden border-2 flex-shrink-0 ${
                      selectedImage === index ? 'border-emerald-500' : 'border-transparent'
                    }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div>
            <div className="flex items-start justify-between gap-4">
              <div>
                <h1 className="font-manrope text-2xl md:text-3xl font-bold text-slate-900">
                  {product.name}
                </h1>
                <button 
                  onClick={() => navigate(`/company/${product.company_id}`)}
                  className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mt-2"
                >
                  <Building2 className="w-4 h-4" />
                  {product.company_name}
                </button>
              </div>
              <Button variant="outline" size="icon" onClick={handleSaveSupplier}>
                <Bookmark className="w-4 h-4" />
              </Button>
            </div>

            <div className="flex flex-wrap gap-2 mt-4">
              <Badge variant="outline">{isUSA && product.category === 'telas' ? 'Fabrics' : product.category}</Badge>
              <Badge variant="outline">{product.subcategory}</Badge>
              {product.origin === 'importado' && (
                <Badge className="bg-blue-100 text-blue-700">{isUSA ? 'Imported' : 'Importado'}</Badge>
              )}
              {product.origin === 'nacional' && (
                <Badge className="bg-green-100 text-green-700">{isUSA ? 'Domestic' : 'Nacional'}</Badge>
              )}
              <Badge className={product.availability === 'disponible' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'}>
                {product.availability === 'disponible' ? (isUSA ? 'Available' : 'Disponible') : 
                 product.availability === 'agotado' ? (isUSA ? 'Out of stock' : 'Agotado') : (isUSA ? 'Made to order' : 'Por pedido')}
              </Badge>
            </div>

            {/* Price */}
            <div className="mt-6">
              {!product.hide_price && product.reference_price ? (
                <div>
                  <span className="text-3xl font-bold text-slate-900">
                    ${product.reference_price.toLocaleString()} {product.currency || 'MXN'}/{product.unit || (product.category === 'telas' ? 'Mt' : (isUSA ? 'Unit' : 'pza'))}
                  </span>
                  <span className="text-slate-500 ml-2">{isUSA ? 'reference price' : 'precio referencial'}</span>
                </div>
              ) : (
                <span className="text-lg text-slate-600">{isUSA ? 'Price upon request' : 'Precio bajo cotización'}</span>
              )}
            </div>

            {/* Description */}
            <div className="mt-6">
              <h3 className="font-semibold text-slate-900 mb-2">{isUSA ? 'Description' : 'Descripción'}</h3>
              <p className="text-slate-600">{isUSA && product.description_en ? product.description_en : product.description}</p>
            </div>

            {/* Specs */}
            <Card className="mt-6">
              <CardContent className="p-4 grid grid-cols-2 gap-4 text-sm">
                {product.composition && (
                  <div>
                    <span className="text-slate-500">{isUSA ? 'Composition' : 'Composición'}</span>
                    <p className="font-medium">{product.composition}</p>
                  </div>
                )}
                {product.weight_gsm && (
                  <div>
                    <span className="text-slate-500">{isUSA ? 'Weight' : 'Gramaje'}</span>
                    <p className="font-medium">{product.weight_gsm} g/m²</p>
                  </div>
                )}
                {product.width_cm && (
                  <div>
                    <span className="text-slate-500">{isUSA ? 'Width' : 'Ancho'}</span>
                    <p className="font-medium">{product.width_cm} cm</p>
                  </div>
                )}
                {product.moq && (
                  <div>
                    <span className="text-slate-500">MOQ</span>
                    <p className="font-medium">
                      {product.moq.toLocaleString()} {product.unit || (product.category === 'telas' ? 'Mt' : (isUSA ? 'Units' : 'pzas'))}
                    </p>
                  </div>
                )}
                {product.delivery_time && (
                  <div>
                    <span className="text-slate-500">{isUSA ? 'Delivery time' : 'Tiempo de entrega'}</span>
                    <p className="font-medium">{product.delivery_time}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Colors */}
            {product.available_colors && product.available_colors.length > 0 && (
              <div className="mt-6">
                <h3 className="font-semibold text-slate-900 mb-2">{isUSA ? 'Available colors' : 'Colores disponibles'}</h3>
                <div className="flex flex-wrap gap-2">
                  {product.available_colors.map((color, index) => (
                    <Badge key={index} variant="outline">{color}</Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="mt-8 space-y-3">
              {/* Quote Dialog */}
              <Dialog open={quoteDialogOpen} onOpenChange={setQuoteDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="w-full bg-slate-900 hover:bg-slate-800" data-testid="request-quote-btn">
                    <FileText className="mr-2 w-4 h-4" />
                    {isUSA ? 'Request quote' : 'Solicitar cotización'}
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>{isUSA ? 'Request quote' : 'Solicitar cotización'}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleQuoteSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <Label>{isUSA ? 'Quantity required *' : 'Cantidad requerida *'}</Label>
                      <div className="flex gap-2">
                        <Input
                          type="number"
                          value={quoteForm.quantity}
                          onChange={(e) => setQuoteForm({ ...quoteForm, quantity: e.target.value })}
                          placeholder="1000"
                          required
                          className="flex-1"
                        />
                        <Select
                          value={quoteForm.unit}
                          onValueChange={(value) => setQuoteForm({ ...quoteForm, unit: value })}
                        >
                          <SelectTrigger className="w-28">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="metros">{isUSA ? 'Meters' : 'Metros'}</SelectItem>
                            <SelectItem value="kilos">{isUSA ? 'Kilos' : 'Kilos'}</SelectItem>
                            <SelectItem value="piezas">{isUSA ? 'Pieces' : 'Piezas'}</SelectItem>
                            <SelectItem value="rollos">{isUSA ? 'Rolls' : 'Rollos'}</SelectItem>
                            <SelectItem value="yardas">{isUSA ? 'Yards' : 'Yardas'}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>{isUSA ? 'Delivery city *' : 'Ciudad de entrega *'}</Label>
                      <Input
                        value={quoteForm.delivery_city}
                        onChange={(e) => setQuoteForm({ ...quoteForm, delivery_city: e.target.value })}
                        placeholder={isUSA ? 'Los Angeles' : 'Ciudad de México'}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{isUSA ? 'Additional specifications' : 'Especificaciones adicionales'}</Label>
                      <Textarea
                        value={quoteForm.specifications}
                        onChange={(e) => setQuoteForm({ ...quoteForm, specifications: e.target.value })}
                        placeholder={isUSA ? 'Detail your requirements...' : 'Detalla tus requerimientos...'}
                      />
                    </div>
                    <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700" disabled={submitting}>
                      {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (isUSA ? 'Submit request' : 'Enviar solicitud')}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>

              {/* Sample Dialog */}
              {product.sample_available && (
                <Dialog open={sampleDialogOpen} onOpenChange={setSampleDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="w-full" data-testid="request-sample-btn">
                      <TestTube className="mr-2 w-4 h-4" />
                      {isUSA ? 'Request physical sample' : 'Solicitar muestra física'}
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-md">
                    <DialogHeader>
                      <DialogTitle>{isUSA ? 'Request physical sample' : 'Solicitar muestra física'}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSampleSubmit} className="space-y-4">
                      <div className="space-y-2">
                        <Label>{isUSA ? 'Shipping address *' : 'Dirección de envío *'}</Label>
                        <Textarea
                          value={sampleForm.shipping_address}
                          onChange={(e) => setSampleForm({ ...sampleForm, shipping_address: e.target.value })}
                          placeholder={isUSA ? 'Street, city, state, ZIP...' : 'Calle, número, colonia, ciudad, CP...'}
                          required
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>{isUSA ? 'Quantity' : 'Cantidad'}</Label>
                          <div className="flex gap-2">
                            <Input
                              type="number"
                              min="1"
                              value={sampleForm.quantity}
                              onChange={(e) => setSampleForm({ ...sampleForm, quantity: parseInt(e.target.value) })}
                              className="flex-1"
                            />
                            <Select
                              value={sampleForm.unit}
                              onValueChange={(value) => setSampleForm({ ...sampleForm, unit: value })}
                            >
                              <SelectTrigger className="w-24">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="metros">{isUSA ? 'Meters' : 'Metros'}</SelectItem>
                                <SelectItem value="kilos">{isUSA ? 'Kilos' : 'Kilos'}</SelectItem>
                                <SelectItem value="piezas">{isUSA ? 'Pieces' : 'Piezas'}</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>{isUSA ? 'Urgency' : 'Urgencia'}</Label>
                          <Select
                            value={sampleForm.urgency}
                            onValueChange={(value) => setSampleForm({ ...sampleForm, urgency: value })}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="normal">Normal</SelectItem>
                              <SelectItem value="urgente">{isUSA ? 'Urgent' : 'Urgente'}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>{isUSA ? 'Comments' : 'Comentarios'}</Label>
                        <Textarea
                          value={sampleForm.comments}
                          onChange={(e) => setSampleForm({ ...sampleForm, comments: e.target.value })}
                          placeholder={isUSA ? 'Additional comments...' : 'Comentarios adicionales...'}
                        />
                      </div>
                      <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700" disabled={submitting}>
                        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (isUSA ? 'Request sample' : 'Solicitar muestra')}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              )}

              {/* Order Button */}
              <Button 
                className="w-full hover:opacity-90"
                style={{ backgroundColor: '#34B772' }}
                onClick={() => setOrderDialogOpen(true)}
                data-testid="make-order-btn"
              >
                <ShoppingCart className="mr-2 w-4 h-4" />
                {isUSA ? 'Place order' : 'Hacer pedido'}
              </Button>

              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => navigate('/dashboard/texvision')}
              >
                <Sparkles className="mr-2 w-4 h-4" />
                {isUSA ? 'View on garment with AI' : 'Ver en prenda con IA'}
              </Button>

              {product.tech_sheet_url && (
                <a href={product.tech_sheet_url} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="w-full">
                    <Package className="mr-2 w-4 h-4" />
                    {isUSA ? 'Download tech sheet' : 'Descargar ficha técnica'}
                  </Button>
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Order Form Modal */}
      {product && (
        <OrderForm
          product={product}
          isOpen={orderDialogOpen}
          onClose={() => setOrderDialogOpen(false)}
        />
      )}

      {/* Pro Paywall Modal */}
      <ProPaywall 
        isOpen={showPaywall} 
        onClose={() => setShowPaywall(false)} 
        feature={isUSA ? 'send quote requests' : 'enviar cotizaciones'}
      />
    </div>
  );
};

export default ProductDetail;
